import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Logo from "../components/Logo";
import { checkoutStatus } from "../lib/api";

const WelcomePage = () => {
  const [params] = useSearchParams();
  const isFoundingParam = params.get("founding") === "1";
  const numberParam = params.get("number");
  const session = params.get("session");
  const planParam = params.get("plan") || "starter";

  const [confetti, setConfetti] = useState(true);
  const [status, setStatus] = useState(session ? "polling" : "missing");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempt, setAttempt] = useState(0);

  // Poll Stripe-backed status endpoint (handles webhook lag)
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    let timer = null;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const data = await checkoutStatus(session);
        if (cancelled) return;
        setPaymentInfo(data);
        if (data.payment_status === "paid" || data.status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (n >= 8) {
          // Webhook should have landed by now — still show success since user came via success_url
          setStatus("pending_confirmation");
          return;
        }
        timer = setTimeout(() => { setAttempt(n + 1); poll(n + 1); }, 2200);
      } catch (e) {
        if (n >= 8) { setStatus("error"); return; }
        timer = setTimeout(() => { setAttempt(n + 1); poll(n + 1); }, 2500);
      }
    };
    poll(0);

    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [session]);

  useEffect(() => {
    const t = setTimeout(() => setConfetti(false), 4500);
    return () => clearTimeout(t);
  }, []);

  const isFounding = paymentInfo?.is_founding ?? isFoundingParam;
  const plan = paymentInfo?.plan || planParam;

  return (
    <div data-testid="welcome-page" style={{ minHeight: "70vh", padding: "60px 20px", textAlign: "center" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <Logo variant="dark" size={80} testId="welcome-logo" />
        </div>

        {status === "polling" && (
          <>
            <p style={{ color: "#5a5d66", fontFamily: "Bebas Neue, sans-serif", letterSpacing: 4, fontSize: 16, margin: 0 }}>
              CONFIRMING YOUR PAYMENT…
            </p>
            <h1 className="font-display" style={{ fontSize: 48, color: "#0A1628", margin: "12px 0" }}>
              ONE MOMENT.
            </h1>
            <p style={{ fontSize: 18, color: "#5a5d66" }}>
              Talking to Stripe… {attempt > 0 && `(attempt ${attempt + 1}/9)`}
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <p data-testid="welcome-paid" style={{ color: "#1A7A4A", fontFamily: "Bebas Neue, sans-serif", letterSpacing: 4, fontSize: 16, margin: 0 }}>
              ✓ PAYMENT CONFIRMED
            </p>
            <h1 className="font-display" data-testid="welcome-headline" style={{ fontSize: 60, color: "#0A1628", margin: "12px 0" }}>
              {isFounding ? `WELCOME, FOUNDING #${numberParam || "—"}.` : `WELCOME TO NEARNOW.`}
            </h1>
            <p style={{ fontSize: 22, color: "#1A1A2E", maxWidth: 560, margin: "0 auto" }}>
              Your business is going live. We'll text you the moment your first customer request comes in.
            </p>
          </>
        )}

        {status === "pending_confirmation" && (
          <>
            <p style={{ color: "#D4AF37", fontFamily: "Bebas Neue, sans-serif", letterSpacing: 4, fontSize: 16, margin: 0 }}>
              YOU'RE IN — RECEIPT ON ITS WAY
            </p>
            <h1 className="font-display" style={{ fontSize: 60, color: "#0A1628", margin: "12px 0" }}>
              WELCOME TO NEARNOW.
            </h1>
            <p style={{ fontSize: 19, color: "#5a5d66" }}>
              Stripe is finalising the confirmation. You'll get a receipt by email within 1 minute.
            </p>
          </>
        )}

        {status === "expired" && (
          <>
            <h1 className="font-display" style={{ fontSize: 48, color: "#A32D2D", margin: "12px 0" }}>
              CHECKOUT EXPIRED
            </h1>
            <p style={{ fontSize: 18 }}>
              Your checkout session timed out. No charge was made. Try again below.
            </p>
            <Link to="/pricing"><button className="btn-gold tap" style={{ marginTop: 18 }}>TRY AGAIN</button></Link>
          </>
        )}

        {status === "missing" && (
          <>
            <h1 className="font-display" style={{ fontSize: 48, color: "#0A1628", margin: "12px 0" }}>
              NOTHING TO CONFIRM HERE
            </h1>
            <p style={{ fontSize: 18 }}>Looks like you got here without checking out.</p>
            <Link to="/pricing"><button className="btn-gold tap" style={{ marginTop: 18 }}>SEE PRICING</button></Link>
          </>
        )}

        {/* Payment summary card — shown for paid + pending_confirmation */}
        {(status === "paid" || status === "pending_confirmation") && (
          <div style={{ marginTop: 30, padding: 24, background: "#0A1628", color: "#fff", borderRadius: 10, textAlign: "left", display: "inline-block" }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 24px", fontSize: 17 }}>
              <span style={{ color: "#D4AF37" }}>Plan:</span><span>{plan.toUpperCase()}</span>
              <span style={{ color: "#D4AF37" }}>Status:</span>
              <span>{isFounding ? "FOUNDING · First month FREE" : "Active"}</span>
              {paymentInfo?.amount_total !== undefined && (
                <>
                  <span style={{ color: "#D4AF37" }}>Amount:</span>
                  <span>
                    ${(paymentInfo.amount_total / 100).toFixed(2)}{" "}
                    {isFounding && <span style={{ color: "#1A7A4A" }}>(charged at day 31)</span>}
                  </span>
                </>
              )}
              <span style={{ color: "#D4AF37" }}>Session:</span>
              <span style={{ fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>{session}</span>
            </div>
          </div>
        )}

        <div style={{ marginTop: 34 }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <button className="btn-gold tap" style={{ fontSize: 22 }}>BACK TO HOME</button>
          </Link>
        </div>

        {confetti && status === "paid" && (
          <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 5 }}>
            {[...Array(36)].map((_, i) => (
              <span key={i} style={{
                position: "absolute", left: `${(i * 37) % 100}%`, top: `-${20 + (i * 13) % 200}px`,
                color: i % 2 ? "#D4AF37" : "#0A1628",
                fontSize: 22, animation: `fall ${4 + (i % 6) * 0.4}s linear forwards`,
              }}>★</span>
            ))}
            <style>{`@keyframes fall { from { transform: translateY(0); opacity: 1; } to { transform: translateY(110vh); opacity: 0; } }`}</style>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePage;
